/**
 * AnalyticsService — the single PostHog seam for Workplace Visitors.
 *
 * Errors first, product analytics later (pageviews + events). One service so
 * the rest of the app never imports `posthog-js` directly.
 *
 * NO-OP unless PLATFORM_MODE is on AND a POSTHOG_KEY is present — demo builds
 * and un-keyed platform builds skip init entirely. Every method is a guarded
 * try/catch: telemetry must never break the app.
 */

import { Injectable } from '@angular/core';
import posthog from 'posthog-js';

import { RDX_VISITORS_ENV } from './env';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private enabled = false;

  /** Initialise PostHog once. Safe to call repeatedly; no-op without a key. */
  init(): void {
    if (this.enabled) return;
    const key = RDX_VISITORS_ENV.POSTHOG_KEY;
    if (!RDX_VISITORS_ENV.PLATFORM_MODE || !key) return; // demo / no key → telemetry off
    try {
      posthog.init(key, {
        api_host: RDX_VISITORS_ENV.POSTHOG_HOST,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: true,
        disable_session_recording: true,
        capture_exceptions: false,
        before_send: (event) => window.location.pathname.startsWith('/v/') ? null : event,
        person_profiles: 'identified_only',
        persistence: 'localStorage+cookie',
      });
      this.enabled = true;
      // A lightweight lifecycle event on every platform load — gives an
      // app-open metric and an immediate signal that ingestion works.
      this.capture('app_opened');
    } catch (e) {
      // Telemetry must never break boot.
      console.error('[analytics] PostHog init failed', e);
    }
  }

  /** Whether PostHog actually initialised (key present + init succeeded). */
  get isEnabled(): boolean {
    return this.enabled;
  }

  /** Capture an exception with optional structured context. */
  captureException(error: unknown, props?: Record<string, unknown>): void {
    if (!this.enabled) return;
    try {
      posthog.captureException(error instanceof Error ? error : new Error(String(error)), props);
    } catch {
      /* swallow — never let capture throw */
    }
  }

  /** Capture a named product event. */
  capture(event: string, props?: Record<string, unknown>): void {
    if (!this.enabled) return;
    try {
      posthog.capture(event, props);
    } catch {
      /* swallow */
    }
  }

  /** Manual SPA pageview (fired on NavigationEnd). */
  pageview(path: string): void {
    if (!this.enabled) return;
    try {
      posthog.capture('$pageview', { $current_url: path });
    } catch {
      /* swallow */
    }
  }

  /** Associate the session with a known user (called once identity resolves). */
  identify(distinctId: string, props?: Record<string, unknown>): void {
    if (!this.enabled) return;
    try {
      posthog.identify(distinctId, props);
    } catch {
      /* swallow */
    }
  }

  /** Clear the identified person (on sign-out). */
  reset(): void {
    if (!this.enabled) return;
    try {
      posthog.reset();
    } catch {
      /* swallow */
    }
  }
}
